/* eslint-disable no-unused-vars */
import React from 'react';
import { Bubble } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';

ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

interface ChartBubbleProps {
  isMulti?: boolean;
  data: any[];
  source?: any;
  brandcount?: number;
  currency?: string;
}

const BubbleChart: React.FC<ChartBubbleProps> = ({ data, currency }) => {
  const uniqueNames = Array.from(new Set(data.flat().map((item) => item.name)));
  const allMonths = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  const dataMap: Record<string, Record<string, any>> = {};
  data.flat().forEach((item) => {
    if (!dataMap[item.name]) {
      dataMap[item.name] = {};
    }
    dataMap[item.name][item.month] = item;
  });

  const completeData = uniqueNames.flatMap((name) => {
    return allMonths.map((month) => {
      return dataMap[name] && dataMap[name][month]
        ? dataMap[name][month]
        : { name, month, value: 0 };
    });
  });

  const maxValue = Math.max(...completeData.map((item) => item.value));

  const monthToNumber = (month: string) => allMonths.indexOf(month);
  const nameToNumber = (name: string) => uniqueNames.indexOf(name);

  const chartData = {
    datasets: [
      {
        data: completeData.map((item) => ({
          x: monthToNumber(item.month),
          y: nameToNumber(item.name), // Add 0.2 to lift bubbles from horizontal line (nameToNumber(item.name) + 0.2)
          r: item.value === 0 ? 1 : Math.sqrt(item.value) / 2,
          value: item.value,
          month: item.month,
          name: item.name,
        })),
        backgroundColor: completeData.map((item) =>
          item.value === maxValue ? '#F9AB01' : '#DBDADE'
        ),
        hoverBackgroundColor: '#F9AB01',
      },
    ],
  };

  const options: ChartOptions<'bubble'> = {
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 10,
        bottom: 0,
        left: 0,
        right: 0,
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        bodyColor: '#000000',
        backgroundColor: '#FFFFFF',
        borderColor: '#E0E0E0',
        borderWidth: 1,
        callbacks: {
          label: (context) => {
            const dataPoint = context.raw as any;
            const value = dataPoint.value;
            const formattedValue = value.toFixed(2);
            return `${dataPoint.name}: ${currency} ${formattedValue} (${dataPoint.month})`;
          },
        },
      },
    },
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        title: { display: true },
        min: 0,
        max: 11,
        ticks: {
          callback: (tickValue) => allMonths[tickValue as number],
          stepSize: 1,
          color: '#4B465C',
        },
        grid: { display: false },
      },
      y: {
        type: 'linear',
        title: { display: true },
        min: 0,
        max: uniqueNames.length - 1,
        ticks: {
          callback: (tickValue) => uniqueNames[tickValue as number] || '',
          stepSize: 1,
          color: '#4B465C',
        },
        // afterFit: (scale) => {
        //   scale.height = scale.height * 1.1;
        // },
        grid: {
          display: true,
          color: '#E0E0E0',
        },
        border: {
          display: false,
        },
      },
    },
  };

  return (
    <div style={{ height: '260px' }}>
      <Bubble data={chartData} options={options} />
    </div>
  );
};

export default BubbleChart;
